import { type PropsWithChildren } from 'react';

import { AppHeader } from '@widgets/AppHeader';
import { AppSidebar } from '@widgets/AppSidebar';
import { usePracticeModal } from '@widgets/NewPracticeModal';

import { SidebarInset, SidebarProvider } from '@shared/ui/sidebar';

interface MainLayoutProps {
  breadcrumbs?: { label: string; href?: string }[];
  headerMode?: 'flow' | 'overlay';
  primaryAction?: {
    label: string;
    icon?: 'new' | 'start';
    onClick?: () => void;
  };
}

export const MainLayout = ({
  children,
  breadcrumbs,
  headerMode,
  primaryAction,
}: PropsWithChildren<MainLayoutProps>) => {
  return (
    <MainLayoutFrame
      breadcrumbs={breadcrumbs}
      headerMode={headerMode}
      primaryAction={primaryAction}
    >
      {children}
    </MainLayoutFrame>
  );
};

const MainLayoutFrame = ({
  children,
  breadcrumbs,
  headerMode = 'flow',
  primaryAction,
}: PropsWithChildren<MainLayoutProps>) => {
  const { openPracticeModal } = usePracticeModal();

  const resolvedPrimaryAction =
    primaryAction ??
    ({
      label: 'New Practice',
      icon: 'new',
      onClick: () => {
        openPracticeModal();
      },
    } satisfies MainLayoutProps['primaryAction']);

  const headerContent = (
    <div className="mx-auto w-full max-w-276">
      <AppHeader breadcrumbs={breadcrumbs} primaryAction={resolvedPrimaryAction} />
    </div>
  );

  return (
    <div
      className="
        flex h-svh w-full flex-col
        md:h-dvh md:flex-row
      "
    >
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className="min-h-0">
          <main
            className="
              relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-background-1 py-0
              pointer-coarse:overscroll-auto
              pointer-fine:overscroll-none
            "
          >
            {headerMode === 'overlay' ? (
              <div className="pointer-events-none sticky top-0 z-10 h-0">
                <div className="pointer-events-auto absolute inset-x-0 top-3 px-4">
                  {headerContent}
                </div>
              </div>
            ) : (
              <div className="sticky top-0 z-10 px-4 pt-3">{headerContent}</div>
            )}
            <div className="relative m-auto">{children}</div>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
};
